import { useCallback, useEffect, useRef } from 'react';
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import { Spin } from 'antd';
import quoteServices from '../../../../../services/oneButtonServices/quoteServices';
import { effectiveAnnualUsageKwhForPricing } from '../../../../../helpers/utilityMonthlyDailyToAnnual';
import { useOneButtonStore } from '../../../../../store/oneButtonStore';
import {
    buildDesignEpcByPartner,
    buildTreeCutPartnerColumnCells,
    partnerEpcUnavailableDetailFromApi,
    parseTreeCutDashboardCardKey,
    resolveUtilityCompanyCode,
    treeCutDashboardCardKey,
    treeCutPartnerCellStateKey,
    treeCutScenarioPricingParamKey,
    type DesignEpcCell,
    type EpcDisplayPartner,
    type TreeCutPricingTable,
    type TreeCutPartnerCellState,
} from '../utils/finance';

interface UseTreeCutScenarioPricingArgs {
    solarUtilityData: any;
    dashboardAnnualUsage: number | null | undefined;
    SFannualUsage: any;
    priceQuoteOverrides: any;
    /** Finance partner display name for Solar (same value shown in Products popup). */
    treeCutRowPricingBodyRef: MutableRefObject<Record<string, any>>;
    treeCutRowPricingInFlightRef: MutableRefObject<Record<string, Promise<any>>>;
    modalTotalModulesBase: number | undefined;
    /** Baseline scenario DC watts (ideal layout); used to scale utility annual kWh per tree-cut row. */
    treeCutScenarioBaseWattage?: number;
    activeModule: any;
    setTreeCutPartnerCellState: Dispatch<SetStateAction<Record<string, TreeCutPartnerCellState>>>;
    treeCutPartnerCellState: Record<string, TreeCutPartnerCellState>;
}

export function useTreeCutScenarioPricing({
    solarUtilityData,
    dashboardAnnualUsage,
    SFannualUsage,
    priceQuoteOverrides,
    treeCutRowPricingBodyRef,
    treeCutRowPricingInFlightRef,
    modalTotalModulesBase,
    treeCutScenarioBaseWattage,
    activeModule,
    setTreeCutPartnerCellState,
    treeCutPartnerCellState,
}: UseTreeCutScenarioPricingArgs) {
    type TreeCutPricingScenario = { actualWattage?: number; cutCost?: unknown };
    const utilityCompanyName = useOneButtonStore((s) => s.utilityCompanyName);
    const treeCutPricingBodyByCardRef = useRef<Record<string, unknown>>({});
    const treeCutSystemKwByCardRef = useRef<Record<string, number>>({});
    const prevUtilityCodeRef = useRef<string | null>(null);
    const numericOpportunityId = useOneButtonStore((s) => s.opportunityID);
    
    const resolveTreeCutUtilityCode = useCallback((): string => {
        if (!solarUtilityData) return '';
        const base = (solarUtilityData.SolarUtility ?? {}) as Record<string, unknown>;
        return resolveUtilityCompanyCode(base, utilityCompanyName);
    }, [solarUtilityData, utilityCompanyName]);

    useEffect(() => {
        const code = resolveTreeCutUtilityCode();
        if (prevUtilityCodeRef.current != null && prevUtilityCodeRef.current !== code) {
            treeCutRowPricingBodyRef.current = {};
            treeCutRowPricingInFlightRef.current = {};
            treeCutPricingBodyByCardRef.current = {};
            treeCutSystemKwByCardRef.current = {};
            setTreeCutPartnerCellState({});
        }
        prevUtilityCodeRef.current = code;
    }, [
        resolveTreeCutUtilityCode,
        treeCutRowPricingBodyRef,
        treeCutRowPricingInFlightRef,
        setTreeCutPartnerCellState,
    ]);

    const fetchTreeCutRowPricingBody = useCallback(
        async (
            rowPk: string,
            systemSize: number,
            scenarioDcWatts: number,
            treeCutCost?: number | null,
        ) => {
            const cached = treeCutRowPricingBodyRef.current[rowPk];
            if (cached !== undefined) return cached;

            const inflight = treeCutRowPricingInFlightRef.current[rowPk];
            if (inflight) return inflight;

            const base = (solarUtilityData!.SolarUtility ?? {}) as Record<string, unknown>;
            const fromDashboard =
                typeof dashboardAnnualUsage === 'number' &&
                Number.isFinite(dashboardAnnualUsage) &&
                dashboardAnnualUsage > 0
                    ? Math.round(dashboardAnnualUsage)
                    : null;
            const utilityAnnualKwh =
                fromDashboard ?? effectiveAnnualUsageKwhForPricing(base, SFannualUsage);
            // Tree rows carry `actualWattage` as DC array watts from SetModuleGraph, not annual kWh.
            // Scale site annual usage by production vs baseline so cut scenarios send a sensible kWh to pricing.
            let estimatedAnnualProduction = 0;
            if (utilityAnnualKwh != null && Number.isFinite(utilityAnnualKwh) && utilityAnnualKwh > 0) {
                const baseW =
                    typeof treeCutScenarioBaseWattage === 'number' &&
                    Number.isFinite(treeCutScenarioBaseWattage) &&
                    treeCutScenarioBaseWattage > 0
                        ? treeCutScenarioBaseWattage
                        : null;
                const scenW =
                    typeof scenarioDcWatts === 'number' && Number.isFinite(scenarioDcWatts) && scenarioDcWatts > 0
                        ? scenarioDcWatts
                        : null;
                if (baseW != null && scenW != null) {
                    const ratio = Math.min(2, Math.max(0.25, scenW / baseW));
                    estimatedAnnualProduction = Math.round(utilityAnnualKwh * ratio);
                } else {
                    estimatedAnnualProduction = Math.round(utilityAnnualKwh);
                }
            }
            if (!Number.isFinite(estimatedAnnualProduction) || estimatedAnnualProduction <= 0) {
                // Fallback: if utility annual usage is unavailable, still allow row pricing by
                // using scenario DC watts as a conservative proxy for annual production.
                const scenarioFallback =
                    typeof scenarioDcWatts === 'number' && Number.isFinite(scenarioDcWatts) && scenarioDcWatts > 0
                        ? Math.round(scenarioDcWatts)
                        : 0;
                estimatedAnnualProduction = scenarioFallback;
            }
            if (!Number.isFinite(estimatedAnnualProduction) || estimatedAnnualProduction <= 0) {
                throw new Error(
                    'Estimated annual production must be greater than 0. Please load/update utility usage or re-run tree simulation.',
                );
            }
            if (!Number.isFinite(systemSize) || systemSize <= 0) {
                throw new Error('System size must be greater than 0.');
            }
            const lowSlope = priceQuoteOverrides?.lowSlopeOverride ?? 0;
            const normalSlope = priceQuoteOverrides?.normalSlopeOverride ?? 0;
            const roofPitch =
                normalSlope > 0 && (lowSlope == null || lowSlope === 0)
                    ? 'Normal Slope'
                    : lowSlope > 0
                      ? 'Low Slope'
                      : 'Normal Slope';

            const pricingPayload = {
                state: String(base.StateId ?? ''),
                systemSize,
                estimatedAnnualProduction,
                utilityCompanyCode: resolveUtilityCompanyCode(base, utilityCompanyName),
                batteryCount: 1,
                roofPitch,
                lowRoof: Math.round(Number(lowSlope) || 0),
                standardRoof: Math.round(Number(normalSlope) || 0),
                FinancePartner: '',
                products: ['Solar'],
                opportunityId: numericOpportunityId,
                TreeCutCost:
                    typeof treeCutCost === 'number' && Number.isFinite(treeCutCost) && treeCutCost > 0
                        ? Math.round(treeCutCost)
                        : undefined,
            };

            const p = quoteServices
                .getPricing(pricingPayload)
                .then((response) => {
                    const body = response?.data;
                    treeCutRowPricingBodyRef.current[rowPk] = body;
                    return body;
                })
                .finally(() => {
                    delete treeCutRowPricingInFlightRef.current[rowPk];
                });

            treeCutRowPricingInFlightRef.current[rowPk] = p;
            return p;
        },
        [
            solarUtilityData,
            utilityCompanyName,
            dashboardAnnualUsage,
            SFannualUsage,
            priceQuoteOverrides?.lowSlopeOverride,
            priceQuoteOverrides?.normalSlopeOverride,
            treeCutScenarioBaseWattage,
            treeCutRowPricingBodyRef,
            treeCutRowPricingInFlightRef,
        ],
    );

    const triggerTreeCutScenarioPartnerPricing = useCallback(
        async (scenario: TreeCutPricingScenario, table: TreeCutPricingTable, scenarioRowIndex: number) => {
            if (!solarUtilityData) return;
            const totalMB =
                typeof modalTotalModulesBase === 'number' && Number.isFinite(modalTotalModulesBase)
                    ? modalTotalModulesBase
                    : undefined;
            const modulePower = Number((activeModule as any)?.power) || 0;
            const utilityCode = resolveTreeCutUtilityCode();
            const pk = treeCutScenarioPricingParamKey(
                scenario,
                table,
                scenarioRowIndex,
                totalMB,
                modulePower,
                utilityCode,
            );
            const cellKeyGl = treeCutPartnerCellStateKey(table, scenarioRowIndex, 'GoodLeap');
            const cellKeyIgs = treeCutPartnerCellStateKey(table, scenarioRowIndex, 'IGS');
            const cellKeyLr = treeCutPartnerCellStateKey(table, scenarioRowIndex, 'LightReach');
            if (!pk) {
                const err = { status: 'error' as const, errorDetail: 'Cannot compute pricing for this scenario' };
                setTreeCutPartnerCellState((prev) => ({
                    ...prev,
                    [cellKeyGl]: err,
                    [cellKeyIgs]: err,
                    [cellKeyLr]: err,
                }));
                return;
            }

            delete treeCutRowPricingBodyRef.current[pk];
            delete treeCutRowPricingInFlightRef.current[pk];

            setTreeCutPartnerCellState((prev) => ({
                ...prev,
                [cellKeyGl]: { status: 'loading' },
                [cellKeyIgs]: { status: 'loading' },
                [cellKeyLr]: { status: 'loading' },
            }));

            let systemSize = NaN;
            if (totalMB != null && modulePower > 0) systemSize = (totalMB * modulePower) / 1000;
            else if (typeof scenario.actualWattage === 'number') systemSize = scenario.actualWattage / 1000;
            const scenarioDcWatts = typeof scenario.actualWattage === 'number' ? scenario.actualWattage : NaN;
            if (!Number.isFinite(systemSize) || !Number.isFinite(scenarioDcWatts)) {
                const err = {
                    status: 'error' as const,
                    errorDetail: 'Missing system size or production for this row. Load utility usage and re-run tree simulation.',
                };
                setTreeCutPartnerCellState((prev) => ({ ...prev, [cellKeyGl]: err, [cellKeyLr]: err }));
                return;
            }

            try {
                const treeCutCost =
                    scenario.cutCost != null && Number.isFinite(Number(scenario.cutCost))
                        ? Number(scenario.cutCost)
                        : null;
                const body = await fetchTreeCutRowPricingBody(pk, systemSize, scenarioDcWatts, treeCutCost);
                const cardKey = treeCutDashboardCardKey(table, scenarioRowIndex);
                treeCutPricingBodyByCardRef.current[cardKey] = body;
                treeCutSystemKwByCardRef.current[cardKey] = systemSize;
                const cells = buildTreeCutPartnerColumnCells(body, { systemSizeKw: systemSize });
                const glBlocks = cells.GoodLeap || [];
                const igsBlocks = cells.IGS || [];
                const lrBlocks = cells.LightReach || [];
                setTreeCutPartnerCellState((prev) => ({
                    ...prev,
                    [cellKeyGl]:
                        glBlocks.length > 0
                            ? { status: 'ok', blocks: glBlocks }
                            : {
                                  status: 'error',
                                  errorDetail: partnerEpcUnavailableDetailFromApi(
                                      body,
                                      'GoodLeap',
                                      'No recommended GoodLeap Solar PPA, Loan, or Lease value for this scenario',
                                  ),
                              },
                    [cellKeyIgs]:
                        igsBlocks.length > 0
                            ? { status: 'ok', blocks: igsBlocks }
                            : {
                                  status: 'error',
                                  errorDetail: partnerEpcUnavailableDetailFromApi(
                                      body,
                                      'IGS',
                                      'No recommended IGS Solar Loan, Lease, or PPA value for this scenario',
                                  ),
                              },
                    [cellKeyLr]:
                        lrBlocks.length > 0
                            ? { status: 'ok', blocks: lrBlocks }
                            : {
                                  status: 'error',
                                  errorDetail: partnerEpcUnavailableDetailFromApi(
                                      body,
                                      'LightReach',
                                      'No totalEpcPayment on recommended LightReach Solar PPA offer',
                                  ),
                              },
                }));
            } catch (e: any) {
                if (quoteServices.isPricingRequestAborted(e)) return;
                const message = e?.response?.data?.message || e?.message || 'Pricing failed';
                const err = { status: 'error' as const, errorDetail: String(message) };
                setTreeCutPartnerCellState((prev) => ({
                    ...prev,
                    [cellKeyGl]: err,
                    [cellKeyIgs]: err,
                    [cellKeyLr]: err,
                }));
            }
        },
        [
            solarUtilityData,
            resolveTreeCutUtilityCode,
            modalTotalModulesBase,
            treeCutScenarioBaseWattage,
            activeModule,
            setTreeCutPartnerCellState,
            treeCutRowPricingBodyRef,
            treeCutRowPricingInFlightRef,
            fetchTreeCutRowPricingBody,
        ],
    );

    const renderTreeCutGoodLeapPurchaseOnCard = useCallback(
        (
            _scenario: TreeCutPricingScenario,
            table: TreeCutPricingTable,
            scenarioRowIndex: number,
            options?: { hidePlaceholderUntilLoaded?: boolean; hideErrorPlaceholder?: boolean },
        ) => {
            const hidePlaceholderUntilLoaded = options?.hidePlaceholderUntilLoaded === true;
            const hideErrorPlaceholder = options?.hideErrorPlaceholder === true;
            if (!solarUtilityData) {
                if (hidePlaceholderUntilLoaded) return null;
                return <span className="editor-ai-tree-cutting-card__goodleap-muted" title="Load opportunity utility data on the dashboard">-</span>;
            }
            const cellKey = treeCutPartnerCellStateKey(table, scenarioRowIndex, 'GoodLeap');
            const cell = treeCutPartnerCellState[cellKey];
            if (!cell) {
                if (hidePlaceholderUntilLoaded) return null;
                return (
                    <span className="editor-ai-tree-cutting-card__goodleap-muted" title="Click $ to load GoodLeap and LightReach pricing">
                        -
                    </span>
                );
            }
            if (cell.status === 'loading') return <Spin size="small" />;
            if (cell.status === 'ok' && cell.blocks.length > 0) {
                return (
                    <span className="editor-ai-tree-cutting-card__goodleap-value editor-ai-tree-cutting-card__goodleap-value--primary">
                        {cell.blocks[0]}
                    </span>
                );
            }
            if (hideErrorPlaceholder) return null;
            if (cell.status === 'error') {
                const detail = cell.errorDetail || '';
                const isNotAvailable = /^No\s/i.test(detail);
                if (isNotAvailable) {
                    return (
                        <span className="editor-ai-tree-cutting-card__goodleap-muted">
                            N/A
                        </span>
                    );
                }
                return (
                    <span
                        className="editor-ai-tree-cutting-card__goodleap-muted"
                        style={{ color: '#f97373' }}
                        title={detail}
                    >
                        -
                    </span>
                );
            }
            return (
                <span className="editor-ai-tree-cutting-card__goodleap-muted" title="No GoodLeap pricing available for this scenario">
                    -
                </span>
            );
        },
        [solarUtilityData, treeCutPartnerCellState],
    );

    const renderTreeCutIgsPrimaryOnCard = useCallback(
        (
            _scenario: TreeCutPricingScenario,
            table: TreeCutPricingTable,
            scenarioRowIndex: number,
            options?: { hidePlaceholderUntilLoaded?: boolean; hideErrorPlaceholder?: boolean },
        ) => {
            const hidePlaceholderUntilLoaded = options?.hidePlaceholderUntilLoaded === true;
            const hideErrorPlaceholder = options?.hideErrorPlaceholder === true;
            if (!solarUtilityData) {
                if (hidePlaceholderUntilLoaded) return null;
                return (
                    <span
                        className="editor-ai-tree-cutting-card__goodleap-muted"
                        title="Load opportunity utility data on the dashboard"
                    >
                        -
                    </span>
                );
            }
            const cellKey = treeCutPartnerCellStateKey(table, scenarioRowIndex, 'IGS');
            const cell = treeCutPartnerCellState[cellKey];
            if (!cell) {
                if (hidePlaceholderUntilLoaded) return null;
                return (
                    <span
                        className="editor-ai-tree-cutting-card__goodleap-muted"
                        title="Click $ to load IGS pricing"
                    >
                        -
                    </span>
                );
            }
            if (cell.status === 'loading') return <Spin size="small" />;
            if (cell.status === 'ok' && cell.blocks.length > 0) {
                return (
                    <span className="editor-ai-tree-cutting-card__goodleap-value editor-ai-tree-cutting-card__goodleap-value--primary">
                        {cell.blocks[0]}
                    </span>
                );
            }
            if (hideErrorPlaceholder) return null;
            if (cell.status === 'error') {
                const detail = cell.errorDetail || '';
                const isNotAvailable = /^No\s/i.test(detail);
                if (isNotAvailable) {
                    return (
                        <span className="editor-ai-tree-cutting-card__goodleap-muted">
                            N/A
                        </span>
                    );
                }
                return (
                    <span
                        className="editor-ai-tree-cutting-card__goodleap-muted"
                        style={{ color: '#f97373' }}
                        title={detail}
                    >
                        -
                    </span>
                );
            }
            return (
                <span
                    className="editor-ai-tree-cutting-card__goodleap-muted"
                    title="No IGS pricing available for this scenario"
                >
                    -
                </span>
            );
        },
        [solarUtilityData, treeCutPartnerCellState],
    );

    const renderTreeCutLightReachTotalEpcPaymentOnCard = useCallback(
        (
            _scenario: TreeCutPricingScenario,
            table: TreeCutPricingTable,
            scenarioRowIndex: number,
            options?: { hidePlaceholderUntilLoaded?: boolean; hideErrorPlaceholder?: boolean },
        ) => {
            const hidePlaceholderUntilLoaded = options?.hidePlaceholderUntilLoaded === true;
            const hideErrorPlaceholder = options?.hideErrorPlaceholder === true;
            if (!solarUtilityData) {
                if (hidePlaceholderUntilLoaded) return null;
                return <span className="editor-ai-tree-cutting-card__goodleap-muted" title="Load opportunity utility data on the dashboard">-</span>;
            }
            const cellKey = treeCutPartnerCellStateKey(table, scenarioRowIndex, 'LightReach');
            const cell = treeCutPartnerCellState[cellKey];
            if (!cell) {
                if (hidePlaceholderUntilLoaded) return null;
                return (
                    <span className="editor-ai-tree-cutting-card__goodleap-muted" title="Click $ to load LightReach pricing">
                        -
                    </span>
                );
            }
            if (cell.status === 'loading') return <Spin size="small" />;
            if (cell.status === 'ok' && cell.blocks.length > 0) {
                return (
                    <span className="editor-ai-tree-cutting-card__goodleap-value editor-ai-tree-cutting-card__goodleap-value--primary">
                        {cell.blocks[0]}
                    </span>
                );
            }
            if (hideErrorPlaceholder) return null;
            if (cell.status === 'error') {
                const detail = cell.errorDetail || '';
                const isNotAvailable = /^No\s/i.test(detail);
                if (isNotAvailable) {
                    return (
                        <span className="editor-ai-tree-cutting-card__goodleap-muted">
                            N/A
                        </span>
                    );
                }
                return (
                    <span
                        className="editor-ai-tree-cutting-card__goodleap-muted"
                        style={{ color: '#f97373' }}
                        title={detail}
                    >
                        -
                    </span>
                );
            }
            return (
                <span className="editor-ai-tree-cutting-card__goodleap-muted" title="No LightReach pricing available for this scenario">
                    -
                </span>
            );
        },
        [solarUtilityData, treeCutPartnerCellState],
    );

    const getTreeCutPartnerCellState = useCallback(
        (cardKey: string, partner: EpcDisplayPartner) => {
            const parsed = parseTreeCutDashboardCardKey(cardKey);
            if (!parsed) return undefined;
            return treeCutPartnerCellState[treeCutPartnerCellStateKey(parsed.table, parsed.scenarioRowIndex, partner)];
        },
        [treeCutPartnerCellState],
    );

    const getTreeCutEpcByPartner = useCallback((cardKey: string): Record<EpcDisplayPartner, DesignEpcCell> => {
        const body = treeCutPricingBodyByCardRef.current[cardKey];
        if (!body) {
            return {
                GoodLeap: { status: 'none' },
                IGS: { status: 'none' },
                LightReach: { status: 'none' },
            };
        }
        const systemSizeKw = treeCutSystemKwByCardRef.current[cardKey];
        return buildDesignEpcByPartner(body, { systemSizeKw });
    }, []);

    const isTreeCutEpcLoading = useCallback(
        (cardKey: string): boolean => {
            const parsed = parseTreeCutDashboardCardKey(cardKey);
            if (!parsed) return false;
            for (const partner of ['GoodLeap', 'IGS', 'LightReach'] as const) {
                const cell = treeCutPartnerCellState[
                    treeCutPartnerCellStateKey(parsed.table, parsed.scenarioRowIndex, partner)
                ];
                if (cell?.status === 'loading') return true;
            }
            return false;
        },
        [treeCutPartnerCellState],
    );

    const clearTreeCutEpcCache = useCallback(() => {
        treeCutPricingBodyByCardRef.current = {};
        treeCutSystemKwByCardRef.current = {};
    }, []);

    const ensureTreeCutPricingForCard = useCallback(
        async (
            cardKey: string,
            scenario: TreeCutPricingScenario,
            table: TreeCutPricingTable,
            scenarioRowIndex: number,
        ): Promise<unknown | null> => {
            if (!solarUtilityData) return null;
            const cached = treeCutPricingBodyByCardRef.current[cardKey];
            if (cached !== undefined) return cached;
            await triggerTreeCutScenarioPartnerPricing(scenario, table, scenarioRowIndex);
            return treeCutPricingBodyByCardRef.current[cardKey] ?? null;
        },
        [solarUtilityData, triggerTreeCutScenarioPartnerPricing],
    );

    return {
        triggerTreeCutScenarioPartnerPricing,
        renderTreeCutGoodLeapPurchaseOnCard,
        renderTreeCutIgsPrimaryOnCard,
        renderTreeCutLightReachTotalEpcPaymentOnCard,
        getTreeCutPartnerCellState,
        getTreeCutEpcByPartner,
        isTreeCutEpcLoading,
        ensureTreeCutPricingForCard,
        clearTreeCutEpcCache,
    };
}
